import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Mountain,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertTriangle,
  Download,
  Send,
  FileText,
  Users,
  CreditCard,
  Search,
  LogOut,
  Receipt,
  Calendar,
  Mail,
  CheckCircle,
  XCircle
} from "lucide-react";

const EMAIL_TEMPLATES = [
  { value: "invoice", label: "Invoice" },
  { value: "payment_reminder", label: "Payment Reminder" },
  { value: "past_due_notice", label: "Past Due Notice" },
  { value: "fine_notice", label: "Fine Notice" }
];

const PAYMENT_TYPES = [
  { value: "dues", label: "Dues" },
  { value: "fine", label: "Fine" },
  { value: "deposit", label: "Deposit" },
  { value: "special_assessment", label: "Special Assessment" }
];

const PAYMENT_METHODS = [
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "ach", label: "ACH Transfer" },
  { value: "wire", label: "Wire Transfer" }
];

const AccountantDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [searchOwner, setSearchOwner] = useState("");
  
  // Export state
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  
  // Email dialog state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    template_type: "invoice",
    owner_id: "",
    amount: "",
    description: "",
    due_date: "",
    invoice_number: ""
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Offline payment dialog state
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    owner_id: "",
    amount: "",
    payment_type: "dues",
    payment_method: "check",
    reference_number: "",
    description: "",
    payment_date: new Date().toISOString().split("T")[0]
  });
  const [recordingPayment, setRecordingPayment] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchOwners();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/accounting/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async (search = "") => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await axios.get(`${API}/accounting/owners-contact${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(response.data.owners);
    } catch (error) {
      console.error("Failed to fetch owners:", error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportStartDate) params.append("start_date", exportStartDate);
      if (exportEndDate) params.append("end_date", exportEndDate);
      
      const response = await axios.get(`${API}/accounting/export/quickbooks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `hoa_export_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.owner_id || !emailForm.amount) {
      toast.error("Please select an owner and enter an amount");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/accounting/send-financial-email`, {
        ...emailForm,
        amount: parseFloat(emailForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email queued for delivery");
      setEmailOpen(false);
      setEmailForm({
        template_type: "invoice",
        owner_id: "",
        amount: "",
        description: "",
        due_date: "",
        invoice_number: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.owner_id || !paymentForm.amount) {
      toast.error("Please select an owner and enter an amount");
      return;
    }
    
    setRecordingPayment(true);
    try {
      await axios.post(`${API}/accounting/offline-payment`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment recorded successfully");
      setPaymentOpen(false);
      setPaymentForm({
        owner_id: "",
        amount: "",
        payment_type: "dues",
        payment_method: "check",
        reference_number: "",
        description: "",
        payment_date: new Date().toISOString().split("T")[0]
      });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setRecordingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-success/10 text-success">Completed</Badge>;
      case "pending":
        return <Badge className="bg-warning/10 text-warning">Pending</Badge>;
      case "overdue":
        return <Badge className="bg-destructive/10 text-destructive">Overdue</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-charcoal text-sand sticky top-0 z-50 backdrop-blur-md bg-charcoal/95">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mountain className="w-7 h-7 text-copper" />
            <div>
              <span className="font-serif text-lg font-semibold">Horse Mountain Ranch</span>
              <Badge className="ml-2 bg-success/20 text-success">ACCOUNTANT</Badge>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-sand/70 hidden sm:inline">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-sand/70 hover:text-sand hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-2">
            Accounting Dashboard
          </h1>
          <p className="text-muted-foreground text-lg">
            Manage finances, payments, and generate reports for Horse Mountain Ranch HOA.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.1s' }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Collected</p>
                  <p className="text-2xl font-bold text-success mt-1">
                    ${loading ? "..." : (dashboard?.total_collected || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-6 h-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.2s' }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-warning mt-1">
                    ${loading ? "..." : (dashboard?.total_pending || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-6 h-6 text-warning" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.3s' }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold text-destructive mt-1">
                    ${loading ? "..." : (dashboard?.total_overdue || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.4s' }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fines Outstanding</p>
                  <p className="text-2xl font-bold mt-1">
                    ${loading ? "..." : (dashboard?.total_fines_outstanding || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Receipt className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Export Button */}
          <Dialog>
            <DialogTrigger asChild>
              <Card className="cursor-pointer hover:-translate-y-1 hover:shadow-floating transition-all duration-300 border-border/50 hover:border-primary/50">
                <CardContent className="pt-6 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                    <Download className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold">Export to QuickBooks</h3>
                  <p className="text-xs text-muted-foreground mt-1">CSV Format</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-serif">Export to QuickBooks</DialogTitle>
                <DialogDescription>Export financial data in QuickBooks-compatible CSV format</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={exportStartDate}
                      onChange={(e) => setExportStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={exportEndDate}
                      onChange={(e) => setExportEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleExport} disabled={exporting} className="shadow-button hover:shadow-button-hover">
                  {exporting ? "Exporting..." : "Download CSV"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Send Email Button */}
          <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
            <DialogTrigger asChild>
              <Card className="cursor-pointer hover:-translate-y-1 hover:shadow-floating transition-all duration-300 border-border/50 hover:border-secondary/50">
                <CardContent className="pt-6 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-3">
                    <Mail className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="font-semibold">Send Financial Email</h3>
                  <p className="text-xs text-muted-foreground mt-1">Invoice, Reminder, Notice</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif">Send Financial Email</DialogTitle>
                <DialogDescription>Send invoice, payment reminder, or notice to an owner</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Template</Label>
                  <Select value={emailForm.template_type} onValueChange={(v) => setEmailForm({...emailForm, template_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_TEMPLATES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select value={emailForm.owner_id} onValueChange={(v) => setEmailForm({...emailForm, owner_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map(o => (
                        <SelectItem key={o.owner_id} value={o.owner_id}>
                          {o.first_name} {o.last_name} ({o.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount ($)</Label>
                    <Input
                      type="number"
                      value={emailForm.amount}
                      onChange={(e) => setEmailForm({...emailForm, amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={emailForm.due_date}
                      onChange={(e) => setEmailForm({...emailForm, due_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={emailForm.description}
                    onChange={(e) => setEmailForm({...emailForm, description: e.target.value})}
                    placeholder="Payment description..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
                <Button onClick={handleSendEmail} disabled={sendingEmail} className="shadow-button hover:shadow-button-hover">
                  {sendingEmail ? "Sending..." : "Send Email"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Record Payment Button */}
          <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
            <DialogTrigger asChild>
              <Card className="cursor-pointer hover:-translate-y-1 hover:shadow-floating transition-all duration-300 border-border/50 hover:border-success/50">
                <CardContent className="pt-6 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center mb-3">
                    <CreditCard className="w-6 h-6 text-success" />
                  </div>
                  <h3 className="font-semibold">Record Payment</h3>
                  <p className="text-xs text-muted-foreground mt-1">Check, Cash, ACH</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif">Record Offline Payment</DialogTitle>
                <DialogDescription>Record a check, cash, or ACH payment received</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select value={paymentForm.owner_id} onValueChange={(v) => setPaymentForm({...paymentForm, owner_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map(o => (
                        <SelectItem key={o.owner_id} value={o.owner_id}>
                          {o.first_name} {o.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount ($)</Label>
                    <Input
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Type</Label>
                    <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm({...paymentForm, payment_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reference # (Check #, Transaction ID)</Label>
                  <Input
                    value={paymentForm.reference_number}
                    onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                    placeholder="Optional note"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
                <Button onClick={handleRecordPayment} disabled={recordingPayment} className="shadow-button hover:shadow-button-hover">
                  {recordingPayment ? "Recording..." : "Record Payment"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Owner Contacts */}
          <Card 
            className="cursor-pointer hover:-translate-y-1 hover:shadow-floating transition-all duration-300 border-border/50 hover:border-info/50"
            onClick={() => navigate("/accountant/owners")}
          >
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-info" />
              </div>
              <h3 className="font-semibold">Owner Contacts</h3>
              <p className="text-xs text-muted-foreground mt-1">View & Update</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Recent Transactions</CardTitle>
            <CardDescription>Latest payment activity</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : dashboard?.recent_transactions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No transactions yet</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboard?.recent_transactions?.map((tx) => (
                    <TableRow key={tx.payment_id}>
                      <TableCell className="text-muted-foreground">
                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell className="capitalize">{tx.payment_type?.replace("_", " ")}</TableCell>
                      <TableCell>{tx.owner_id}</TableCell>
                      <TableCell className="text-right font-medium">${tx.amount?.toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(tx.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AccountantDashboard;
